import React, { useState, useCallback, useRef } from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

interface CoquinConfessionsProps {
  confessionsList: string[];
  onBack: () => void;
}

type Phase = 'card' | 'counting' | 'suspense' | 'reveal';

const DEFAULT_CONFESSION = "J'ai déjà fait quelque chose que je n'assumerai jamais... 💣";

export default function CoquinConfessions({ confessionsList, onBack }: CoquinConfessionsProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [currentConfession, setCurrentConfession] = useState(() =>
    confessionsList.length > 0 ? confessionsList[Math.floor(Math.random() * confessionsList.length)] : DEFAULT_CONFESSION
  );
  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState<Phase>('card');
  const [drinkResult, setDrinkResult] = useState<'oui' | 'non' | null>(null);
  const [suspenseText, setSuspenseText] = useState('');
  const lastRef = useRef('');
  const turnCount = useRef(0);

  const handleFlip = useCallback(() => {
    if (isFlipped) return;
    setIsFlipped(true);
    setPhase('counting');
    try { Haptics.impact({ style: ImpactStyle.Medium }); } catch {}
  }, [isFlipped]);

  const handleIncrement = () => {
    setCount(c => c + 1);
    try { Haptics.impact({ style: ImpactStyle.Light }); } catch {}
  };

  const handleReset = () => {
    setCount(0);
  };

  const handleValidate = () => {
    setPhase('suspense');
    const result: 'oui' | 'non' = Math.random() > 0.5 ? 'oui' : 'non';

    const labels = ['OUI ?', 'NON ?', 'OUI ?', 'NON ?', 'OUI ?', 'NON ?', 'OUI ?', 'NON ?'];
    let tick = 0;
    const interval = setInterval(() => {
      setSuspenseText(labels[tick % labels.length]);
      tick++;
      if (tick >= 12) {
        clearInterval(interval);
        setDrinkResult(result);
        setPhase('reveal');
        try { Haptics.impact({ style: ImpactStyle.Heavy }); } catch {}
      }
    }, 160);
  };

  const handleNextCard = () => {
    turnCount.current += 1;
    setIsFlipped(false);
    setCount(0);
    setPhase('card');
    setDrinkResult(null);
    setSuspenseText('');
    setTimeout(() => {
      let next = DEFAULT_CONFESSION;
      if (confessionsList.length > 0) {
        const avail = confessionsList.filter(c => c !== lastRef.current);
        const pool = avail.length > 0 ? avail : confessionsList;
        next = pool[Math.floor(Math.random() * pool.length)];
        lastRef.current = next;
      }
      setCurrentConfession(next);
    }, 300);
  };

  const fontSize = currentConfession.length > 100 ? '16px' : currentConfession.length > 60 ? '18px' : '20px';

  return (
    <div className={`h-screen flex flex-col safe-top safe-bottom overflow-y-auto transition-colors duration-500 ${
      phase === 'reveal' && drinkResult === 'oui' ? 'bg-gradient-to-b from-[#7f1d1d] via-[#5c1010] to-[#2a0505]'
      : phase === 'reveal' && drinkResult === 'non' ? 'bg-gradient-to-b from-[#831843] via-[#5c1010] to-[#2a0505]'
      : 'bg-gradient-to-b from-[#450a0a] via-[#5c1010] to-[#2a0505]'
    }`}>
      {/* Back */}
      <button onClick={onBack} className="absolute left-6 z-20 w-10 h-10 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center text-white/60 btn-press border border-white/10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>←</button>

      {/* Turn counter */}
      <div className="absolute right-6 z-10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>
        <span className="text-white/30 font-body text-sm">Tour {turnCount.current + 1}</span>
      </div>

      <div className="flex-shrink-0 pt-14 pb-2 px-6 text-center">
        <h2 className="font-display font-bold text-xl text-white mb-1">💣 Confessions</h2>
        <p className={`text-pink-300/60 font-body text-sm ${phase === 'card' ? 'tp-pulse' : ''}`}>
          {phase === 'card' && 'Êtes-vous prêts ?'}
          {phase === 'counting' && 'Levez la main si vous aussi...'}
          {phase === 'suspense' && 'Qui va boire ?!'}
          {phase === 'reveal' && 'Le verdict est tombé !'}
        </p>
      </div>

      <div className="flex-1 flex flex-col items-center px-6 pb-6">

        {/* Phase: suspense animation */}
        {phase === 'suspense' && (
          <div className="flex-1 flex items-center justify-center">
            <p className="font-display font-black text-white animate-pulse drop-shadow-lg" style={{ fontSize: '56px' }}>
              {suspenseText}
            </p>
          </div>
        )}

        {/* Phase: reveal result */}
        {phase === 'reveal' && drinkResult && (
          <div className="flex-1 flex flex-col items-center justify-center animate-slide-up">
            <div className="text-center mb-6">
              <span className="text-6xl block mb-4">🍺</span>
              <p className="font-display font-black text-white leading-tight" style={{ fontSize: '28px' }}>
                {drinkResult === 'oui'
                  ? 'Ceux qui ont levé la main boivent !'
                  : 'Ceux qui n\'ont pas levé la main boivent !'
                }
              </p>
            </div>
            <button
              onClick={handleNextCard}
              className="w-full max-w-sm py-4 rounded-full bg-gradient-to-r from-red-500 to-pink-400 text-white font-display font-bold text-base btn-press shadow-[0_4px_20px_rgba(239,68,68,0.3)]"
            >
              Carte suivante →
            </button>
          </div>
        )}

        {/* Phase: card + counting */}
        {(phase === 'card' || phase === 'counting') && (
          <>
            {/* Flip card */}
            <div className={`w-full max-w-sm ${!isFlipped ? 'cursor-pointer tp-breathe' : ''}`} onClick={!isFlipped ? handleFlip : undefined}>
              <div className="tp-card-flip-v w-full" style={{ height: 'min(30vh, 220px)' }}>
                <div className={`tp-card-inner ${isFlipped ? 'tp-flipped' : ''}`}>
                  <div className="tp-card-face tp-card-front">
                    <div className="w-full h-full rounded-3xl bg-gradient-to-br from-red-600 via-red-700 to-red-900 border border-red-400/20 flex items-center justify-center shadow-[0_8px_30px_rgba(239,68,68,0.3)]">
                      <span className="text-7xl opacity-60">💣</span>
                    </div>
                  </div>
                  <div className="tp-card-face tp-card-back-reveal">
                    <div className="w-full h-full rounded-3xl bg-gradient-to-br from-red-500 via-pink-500 to-red-600 border border-pink-400/30 flex flex-col items-center justify-center px-6 py-4 shadow-[0_8px_30px_rgba(244,114,182,0.3)]">
                      <span className="text-2xl mb-2">💣</span>
                      <p className="text-white font-display font-bold text-center leading-relaxed" style={{ fontSize }}>{currentConfession}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Counting phase */}
            {phase === 'counting' && (
              <div className="w-full max-w-sm mt-4 animate-slide-up">
                {/* Counter */}
                <div className="text-center mb-3">
                  <p className="font-display font-black text-white drop-shadow-lg" style={{ fontSize: '48px' }}>{count}</p>
                  <p className="text-pink-300/60 font-body text-sm mt-1">
                    {count === 0 ? 'Personne encore...' : `${count} joueur${count > 1 ? 's' : ''} ${count > 1 ? 'ont' : 'a'} levé la main 👀`}
                  </p>
                </div>

                {/* +1 Button */}
                <button
                  onClick={handleIncrement}
                  className="w-full py-5 rounded-2xl bg-gradient-to-r from-pink-500 to-red-500 text-white font-display font-black text-2xl btn-press shadow-[0_4px_20px_rgba(244,114,182,0.4)] mb-3"
                  style={{ minHeight: '80px' }}
                >
                  ✋ +1
                </button>

                {/* Reset */}
                <button onClick={handleReset} className="w-full py-2.5 rounded-xl bg-white/10 text-white/50 font-body text-xs btn-press border border-white/10 mb-4">
                  Réinitialiser
                </button>

                {/* Validate button */}
                <button
                  onClick={handleValidate}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-red-500 to-pink-400 text-white font-display font-bold text-lg btn-press shadow-[0_4px_20px_rgba(239,68,68,0.4)] animate-pulse-glow"
                >
                  Découvrir qui boit 🎲
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
